/**
 * ASGTRANS's two legal documents as public pages of the portal: which exist, and at what addresses
 * (design: `docs/superpowers/specs/2026-10-05-deca-privacy-policy-design.md` §5.1).
 *
 * One list, imported by the build that writes the pages (`legal/plugin.ts`) and by the portal's
 * links (`legal/LegalLinks.tsx`, the user menu), so a link and the file it points at cannot drift
 * apart. The addresses are fixed: the App Store and Play consoles and ASG Mobile hold them, and no
 * revision of the text changes them. English lives under `/en/` with the same name — the rule the
 * help-pages plan set, so each document has one name and the prefix alone says the language.
 */

import type { Language } from '../app/i18n.ts'

export type LegalDocKey = 'privacidad' | 'aviso-legal'

export const LEGAL_DOC_KEYS: readonly LegalDocKey[] = ['privacidad', 'aviso-legal']

export function legalPageUrl(key: LegalDocKey, language: Language): string {
  return language === 'en' ? `/en/${key}` : `/${key}`
}

/**
 * BOE Third.3–4: nothing but a document's own PDF may ever answer under `/d/`. Caddy routes `/d/*`
 * to the API before it looks for a file, so a page there would never be served; refusing to write
 * one states the same rule where the pages are made.
 */
export function assertOutsideDownloadPath(file: string): string {
  if (file === 'd' || file.startsWith('d/')) {
    throw new Error(`a legal page may not be written under /d/: ${file}`)
  }
  return file
}

/**
 * The file the build writes for a page: its address plus `.html`, which Caddy's
 * `try_files {path} {path}.html` serves without the extension. Never a folder's `index.html`: a
 * directory address falls through to the portal (measured for `/api-docs`).
 */
export function legalPageFile(key: LegalDocKey, language: Language): string {
  return assertOutsideDownloadPath(`${legalPageUrl(key, language).slice(1)}.html`)
}

/** Every page's address, for the development server. */
export const LEGAL_PAGE_ROUTES: ReadonlyMap<string, { key: LegalDocKey; language: Language }> = new Map(
  LEGAL_DOC_KEYS.flatMap((key) =>
    (['es', 'en'] as const).map((language) => [legalPageUrl(key, language), { key, language }] as const),
  ),
)
